import { OpenAPI } from 'app/API';
import { getRefreshPromise } from 'app/http';
import { ApiCallError, TRefreshOutcome } from 'app/http/ApiCallError';
import { invalidateSession } from 'app/http/sessionInvalidation';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

type APIResponse = { body: unknown; status: number; url: string };

function getBodyMessage(body: unknown): string {
  if (typeof body === 'object' && body !== null && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'Error';
}

function getHTTPError(response: APIResponse, reported = false, refreshOutcome?: TRefreshOutcome): ApiCallError {
  return new ApiCallError(`Request failed with status ${response.status}`, {
    body: response.body,
    status: response.status,
    url: response.url,
    reported,
    refreshOutcome,
  });
}

export function useAPI() {
  const [error, setError] = useState<ApiCallError | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const API_URL = import.meta.env.VITE_API_URL ?? '';

  OpenAPI.BASE = API_URL ?? '';
  OpenAPI.WITH_CREDENTIALS = true;

  const call = useCallback(
    async <T extends APIResponse>(
      request: Promise<T> | (() => Promise<T>),
      options?: {
        onError?: (error: string) => void;
        onSuccess?: (response: T['body']) => void;
        dontCatchErrors?: boolean;
        dontRedirectOnError?: boolean;
      },
    ): Promise<T['body']> => {
      setIsLoading(true);
      setError(undefined);
      try {
        let response = await (typeof request === 'function' ? request() : request);
        let refreshOutcome: TRefreshOutcome | undefined;
        if (response.status === 418) {
          const refreshResult = await getRefreshPromise();
          refreshOutcome = refreshResult.kind;
          if (refreshResult.kind === 'auth-failure') {
            invalidateSession({ broadcast: true });
          } else if (refreshResult.kind === 'success' && typeof request === 'function') {
            response = await request();
          }
        }
        if (response.status === 429) {
          if (window.location.pathname !== '/rate-limit') {
            window.location.pathname = '/rate-limit';
          }
          const apiError = getHTTPError(response);
          setError(apiError);
          throw apiError;
        }
        if (response.status === 412) {
          if (window.location.pathname !== '/account-disabled') {
            window.location.pathname = '/account-disabled';
          }
          const apiError = getHTTPError(response);
          setError(apiError);
          throw apiError;
        }
        if (options?.dontCatchErrors) {
          if (options?.onSuccess) {
            options.onSuccess(response.body);
          }
          return response.body;
        }

        if (response.status === 500) {
          console.error('response.body', response.body);
          console.error('response.url', response.url);
          console.error('response.status', response.status);
          console.error('response', response);

          if (import.meta.env.VITE_DEV_ERROR_DEBUGGING === 'true') {
            toast.error('Server error: ' + getBodyMessage(response.body));
            const apiError = getHTTPError(response);
            setError(apiError);
            throw apiError;
          }

          const apiError = getHTTPError(response, Boolean(options?.dontRedirectOnError && options?.onError));
          if (options?.dontRedirectOnError) {
            options.onError?.(getBodyMessage(response.body));
          } else {
            if (!window.location.href.includes('/errors/500')) {
              localStorage.setItem('previous500page', window.location.href);
              window.location.href = '/errors/500';
            }
          }
          setError(apiError);
          throw apiError;
        }

        if (response.status >= 400) {
          console.error('response.body', response.body);
          console.error('response.url', response.url);
          console.error('response.status', response.status);
          console.error('response', response);
          const apiError = getHTTPError(response, Boolean(options?.onError), refreshOutcome);
          if (response.status === 401) {
            invalidateSession({ broadcast: true });
          }
          setError(apiError);
          if (options?.onError) {
            options.onError(getBodyMessage(response.body));
          }
          throw apiError;
        }
        if (options?.onSuccess) {
          options.onSuccess(response.body);
        }
        return response.body;
      } catch (e: unknown) {
        if (e instanceof ApiCallError || (e instanceof Error && e.name === 'AbortError')) {
          throw e;
        }
        const apiError = new ApiCallError('Network request failed', { cause: e, reported: Boolean(options?.onError) });
        setError(apiError);
        if (options?.onError) {
          options.onError(e instanceof Error ? e.message : String(e));
        }
        throw apiError;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  function dismissError() {
    setError(undefined);
  }

  return { dismissError, error, isLoading, call };
}
